/**
 * Franchise collection service – maintains a dedicated `franchises` collection
 * so /franchises can read from one small collection instead of querying
 * businesses + ddfListings with a franchise filter.
 */

import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  getDocs,
  getCountFromServer,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { BusinessListing } from './businessService';

const FRANCHISES_COLLECTION = 'franchises';

export type FranchiseSource = 'businesses' | 'ddfListings';

/** Doc shape in the franchises collection (denormalized for list view) */
export interface FranchiseDoc {
  source: FranchiseSource;
  listingId: string;
  title: string;
  description: string;
  category: string;
  subcategory: string;
  province: string;
  city: string;
  askingPrice: number;
  currency: string;
  annualRevenue: number;
  annualCashFlow: number;
  featuredImage: string;
  status: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  tags?: string[];
  seoUrl?: string;
}

/** Payload to upsert – minimal fields needed to build FranchiseDoc */
export interface FranchiseUpsertPayload {
  title: string;
  description?: string;
  category: string;
  subcategory?: string;
  province: string;
  city: string;
  askingPrice: number;
  currency: string;
  annualRevenue?: number;
  annualCashFlow?: number;
  featuredImage?: string;
  status: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  tags?: string[];
  seoUrl?: string;
}

function franchiseDocId(source: FranchiseSource, listingId: string): string {
  return `${source}_${listingId}`;
}

/**
 * Upsert a franchise listing into the franchises collection.
 * Call when source listing has franchise === true.
 */
export async function upsertFranchiseListing(
  source: FranchiseSource,
  listingId: string,
  payload: FranchiseUpsertPayload
): Promise<void> {
  const id = franchiseDocId(source, listingId);
  const ref = doc(db, FRANCHISES_COLLECTION, id);
  const docData: FranchiseDoc = {
    source,
    listingId,
    title: payload.title,
    description: payload.description ?? '',
    category: payload.category,
    subcategory: payload.subcategory ?? '',
    province: payload.province,
    city: payload.city,
    askingPrice: payload.askingPrice,
    currency: payload.currency,
    annualRevenue: payload.annualRevenue ?? 0,
    annualCashFlow: payload.annualCashFlow ?? 0,
    featuredImage: payload.featuredImage ?? '',
    status: payload.status,
    createdAt: payload.createdAt,
    updatedAt: payload.updatedAt,
    tags: payload.tags,
    seoUrl: payload.seoUrl,
  };
  await setDoc(ref, docData);
}

/**
 * Remove a listing from the franchises collection.
 * Call when source listing has franchise === false or is deleted.
 */
export async function removeFranchiseListing(
  source: FranchiseSource,
  listingId: string
): Promise<void> {
  const id = franchiseDocId(source, listingId);
  const ref = doc(db, FRANCHISES_COLLECTION, id);
  await deleteDoc(ref);
}

/**
 * Map a FranchiseDoc to BusinessListing-like shape for the grid.
 * id uses ddf- prefix when source is ddfListings so detail links work.
 */
function franchiseDocToBusinessListing(fd: FranchiseDoc & { id: string }): BusinessListing {
  const displayId = fd.source === 'ddfListings' ? `ddf-${fd.listingId}` : fd.listingId;
  return {
    id: displayId,
    basic: {
      title: fd.title,
      description: fd.description,
      shortDescription: fd.description?.substring(0, 200) ?? '',
      category: fd.category,
      subcategory: fd.subcategory,
      tags: fd.tags ?? [],
      status: fd.status as BusinessListing['basic']['status'],
      featured: false,
      verified: false,
      createdAt: fd.createdAt,
      updatedAt: fd.updatedAt,
      expiresAt: fd.updatedAt,
      seoUrl: fd.seoUrl,
    },
    location: {
      address: '',
      city: fd.city,
      province: fd.province,
      postalCode: '',
      country: 'Canada',
      coordinates: { latitude: 0, longitude: 0 } as any,
      region: fd.province,
      timezone: 'America/Toronto',
    },
    financial: {
      askingPrice: fd.askingPrice,
      currency: fd.currency,
      annualRevenue: fd.annualRevenue,
      annualCashFlow: fd.annualCashFlow,
      annualExpenses: 0,
      profitMargin: 0,
      growthRate: 0,
      pricePerRevenue: 0,
      pricePerCashFlow: 0,
      financingAvailable: false,
    },
    business: {
      established: 0,
      employees: { min: 0, max: 0 },
      squareFootage: 0,
      leaseTerms: '',
      reasonForSale: '',
      trainingIncluded: false,
      relocatable: false,
      workFromHome: false,
      absenteeOwnership: false,
      franchise: true,
    },
    media: {
      images: fd.featuredImage ? [fd.featuredImage] : [],
      videos: [],
      documents: [],
      featuredImage: fd.featuredImage,
    },
    owner: {
      userId: '',
      contactInfo: { name: '', email: '', phone: '', preferredContact: 'email' },
      responseTime: 24,
    },
    search: {
      searchableText: '',
      priceRange: 'under_100k',
      revenueRange: 'under_100k',
      locationKeywords: [],
      categoryKeywords: [],
    },
  } as BusinessListing;
}

export interface FranchiseFilters {
  category?: string;
  subcategory?: string;
  province?: string;
  priceMin?: number;
  priceMax?: number;
}

export interface FranchiseSort {
  field: 'createdAt' | 'updatedAt' | 'askingPrice';
  direction: 'asc' | 'desc';
}

/**
 * Get total count of franchise listings in the franchises collection
 * (status in active, pending, sold, expired).
 */
export async function getTotalFranchiseCount(): Promise<number> {
  const q = query(
    collection(db, FRANCHISES_COLLECTION),
    where('status', 'in', ['active', 'pending', 'sold', 'expired'])
  );
  const snapshot = await getCountFromServer(q);
  return snapshot.data().count;
}

/**
 * Get franchise listings from the franchises collection.
 * Returns BusinessListing-shaped items so the existing grid works unchanged.
 */
export async function getFranchiseListings(
  filters: FranchiseFilters = {},
  sort: FranchiseSort = { field: 'createdAt', direction: 'desc' },
  pageSize: number = 100,
  lastDoc?: any
): Promise<{ businesses: BusinessListing[]; total: number; hasMore: boolean; lastDoc: any }> {
  const q = collection(db, FRANCHISES_COLLECTION);
  const constraints: any[] = [];

  constraints.push(where('status', 'in', ['active', 'pending', 'sold', 'expired']));
  if (filters.province) {
    constraints.push(where('province', '==', filters.province));
  }
  if (filters.category) {
    constraints.push(where('category', '==', filters.category));
  }
  if (filters.subcategory) {
    constraints.push(where('subcategory', '==', filters.subcategory));
  }
  if (filters.priceMin !== undefined && filters.priceMin > 0) {
    constraints.push(where('askingPrice', '>=', filters.priceMin));
  }
  if (filters.priceMax !== undefined && filters.priceMax > 0) {
    constraints.push(where('askingPrice', '<=', filters.priceMax));
  }

  const sortField = sort.field === 'askingPrice' ? 'askingPrice' : sort.field;
  constraints.push(orderBy(sortField, sort.direction));
  constraints.push(limit(pageSize));
  if (lastDoc) {
    constraints.push(startAfter(lastDoc));
  }

  const snapshot = await getDocs(query(q, ...constraints));
  const businesses: BusinessListing[] = [];
  snapshot.forEach((d) => {
    const data = d.data() as FranchiseDoc;
    businesses.push(franchiseDocToBusinessListing({ id: d.id, ...data }));
  });

  return {
    businesses,
    total: businesses.length,
    hasMore: snapshot.docs.length === pageSize,
    lastDoc: snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : undefined,
  };
}
